import { useQuery, useLazyQuery } from '@apollo/client';
import { useMemo } from 'react';
import useTranslatedText from '../../../hooks/useTranslatedText';
import {
  COMPLETIONS_V3,
  GENDERLESS_SEARCH,
} from '../queries';
import getCommaSeparatedStringValues from '../tools/getCommaSeparatedStringValues';

/**
 * Returns the Brand Specific Popular Searches TMNT. Abercrombie Popular
 * Searches are returned if no brand is provided.
 * @returns {any} Array of brand Specific Popular searches spliced from TMNT
 * or null if the TMNT is blank.
 */
const usePopularSearches = () => {
  const popularSearches = useTranslatedText('popularSearches');
  const popularSearchesList = popularSearches?.value;

  return popularSearchesList ? (getCommaSeparatedStringValues(popularSearchesList)) : [];
};

/**
 * Custom Hook to fetch and memoize product type suggestions.
 *
 * @returns {[Function, Object]} A tuple containing function to trigger the suggestions query,
 * and the second element is an object containing:
 *   - productTypes: {Array} List of suggested product types.
 *   - products: {Array} List of suggested products.
 *   - combinedSuggestions: {Array} Combined list of product types and products.
 */
const useSuggestions = () => {
  const [getSuggestions, { data: autoSuggestions, previousData }] = useLazyQuery(COMPLETIONS_V3, {
    ssr: false,
  });

  const suggestions = useMemo(() => {
    // Use current data if available, otherwise fall back to previous data
    const activeData = autoSuggestions || previousData;

    if (!activeData) {
      return {
        productTypes: [],
        products: [],
        combinedSuggestions: [],
      };
    }

    const productTypes = activeData.productTypes?.suggestedItems || [];
    const products = activeData.products?.suggestedItems || [];
    const combinedSuggestions = [...productTypes, ...products];

    return {
      productTypes,
      products,
      combinedSuggestions,
    };
  }, [autoSuggestions, previousData]);

  return [getSuggestions, suggestions];
};

const useGenderLessSearch = () => {
  const { loading, error, data } = useQuery(GENDERLESS_SEARCH);
  if (loading || error) return false;

  const {
    genderLessSearch,
  } = data.config ?? {
    genderLessSearch: { value: false },
  };

  return genderLessSearch.value;
};

export {
  usePopularSearches,
  useSuggestions,
  useGenderLessSearch,
};
